import { Directive, ElementRef, OnInit, OnDestroy, AfterViewInit } from '@angular/core';

declare const THREE: any;

@Directive({
  selector: '[appHeroCanvas]',
  standalone: false
})
export class HeroCanvasDirective implements OnInit, AfterViewInit, OnDestroy {
  private scene: any;
  private camera: any;
  private renderer: any;
  private points: any;
  private ringGroup: any;
  private cubeGroup: any;
  private animationId: number;
  private mouseX: number = 0;
  private mouseY: number = 0;

  constructor(private el: ElementRef) {}

  ngOnInit() {}

  ngAfterViewInit() {
    if (typeof THREE !== 'undefined') {
      setTimeout(() => {
        this.initThreeJS();
      }, 100);
    }
  }

  private initThreeJS() {
    const canvas = this.el.nativeElement;
    const parent = canvas.parentElement;
    
    if (!parent || typeof THREE === 'undefined') return;

    // Scene setup
    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(
      60,
      parent.offsetWidth / parent.offsetHeight,
      0.1,
      1000
    );
    
    this.renderer = new THREE.WebGLRenderer({ 
      canvas: canvas, 
      alpha: true, 
      antialias: true 
    });
    this.renderer.setPixelRatio(window.devicePixelRatio || 1);
    this.renderer.setSize(parent.offsetWidth, parent.offsetHeight);

    // Lighting
    const ambient = new THREE.AmbientLight(0x404050, 1.2);
    this.scene.add(ambient);
    const pLight = new THREE.PointLight(0x00AAE6, 1.2, 50);
    pLight.position.set(5, 5, 5);
    this.scene.add(pLight);

    // Particles
    const count = 1200;
    const positions = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      const i3 = i * 3;
      positions[i3] = (Math.random() - 0.5) * 18;
      positions[i3 + 1] = (Math.random() - 0.5) * 6;
      positions[i3 + 2] = (Math.random() - 0.5) * 18;
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    const material = new THREE.PointsMaterial({
      color: 0x00AAE6,
      size: 0.04,
      transparent: true,
      opacity: 0.45
    });
    this.points = new THREE.Points(geometry, material);
    this.scene.add(this.points);

    // Ring
    this.ringGroup = new THREE.Group();
    const tor = new THREE.TorusGeometry(3.2, 0.02, 16, 200);
    const mat = new THREE.MeshBasicMaterial({
      color: 0x00AAE6,
      transparent: true,
      opacity: 0.06
    });
    const mesh = new THREE.Mesh(tor, mat);
    mesh.rotation.x = Math.PI / 2;
    this.ringGroup.add(mesh);
    this.scene.add(this.ringGroup);

    // Wireframe cube
    // this.cubeGroup = new THREE.Group();
    // const cubeGeo = new THREE.BoxGeometry(2.2, 1.6, 2.2);
    // const edges = new THREE.EdgesGeometry(cubeGeo);
    // const lineMat = new THREE.LineBasicMaterial({
    //   color: 0x00AAE6,
    //   linewidth: 1,
    //   transparent: true,
    //   opacity: 0.45
    // });
    // const wire = new THREE.LineSegments(edges, lineMat);
    // wire.position.set(0.6, -0.2, 0);
    // this.cubeGroup.add(wire);

    // const innerMat = new THREE.MeshBasicMaterial({
    //   color: 0x00AAE6,
    //   transparent: true,
    //   opacity: 0.02
    // });
    // const inner = new THREE.Mesh(cubeGeo, innerMat);
    // inner.position.copy(wire.position);
    // this.cubeGroup.add(inner);
    // this.scene.add(this.cubeGroup);

    this.camera.position.z = 5.6;

    // Mouse movement listener
    document.addEventListener('mousemove', this.onMouseMove.bind(this));
    window.addEventListener('resize', this.onWindowResize.bind(this));

    // Start animation
    this.animate();
  }

  private onMouseMove(event: MouseEvent) {
    this.mouseX = (event.clientX / window.innerWidth - 0.5) * 2;
    this.mouseY = (event.clientY / window.innerHeight - 0.5) * -2;
  }

  private onWindowResize() {
    if (!this.camera || !this.renderer) return;
    
    const parent = this.el.nativeElement.parentElement;
    if (!parent) return;

    this.camera.aspect = parent.offsetWidth / parent.offsetHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(parent.offsetWidth, parent.offsetHeight);
  }

  private animate() {
    this.animationId = requestAnimationFrame(() => this.animate());

    // Slow ambient motion
    if (this.points) {
      this.points.rotation.y += 0.0006;
      // Mouse parallax for particles
      this.points.position.x += (this.mouseX * 0.6 - this.points.position.x) * 0.02;
      this.points.position.y += (this.mouseY * 0.4 - this.points.position.y) * 0.02;
    }

    if (this.ringGroup) {
      this.ringGroup.rotation.y += 0.0004;
    }

    // if (this.cubeGroup) {
    //   this.cubeGroup.rotation.y += 0.0015;
    //   this.cubeGroup.rotation.x += 0.0009;
    //   // Mouse parallax for cube
    //   this.cubeGroup.position.x += (this.mouseX * 0.25 - this.cubeGroup.position.x) * 0.03;
    //   this.cubeGroup.position.y += (this.mouseY * 0.18 - this.cubeGroup.position.y) * 0.03;
    // }

    if (this.renderer && this.scene && this.camera) {
      this.renderer.render(this.scene, this.camera);
    }
  }

  ngOnDestroy() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
    }
    document.removeEventListener('mousemove', this.onMouseMove.bind(this));
    window.removeEventListener('resize', this.onWindowResize.bind(this));
    
    // Clean up Three.js resources
    if (this.renderer) {
      this.renderer.dispose();
    }
  }
}
